import type { ReactNode } from 'react'
import type { ChecklistItem, ChecklistScheduleMode } from '../../types/schema'
import { truncateToLimit } from '../../utils/checklistLimits'
import {
  DEFAULT_CURRENCY_CODE,
  getCurrencyScrollStep,
  normalizeRatingStars,
  type RatingDimensionKey,
} from '../../utils/checklistNodeMeta'
import { isDurationChecklist } from '../../services/checklistDurationHelpers'
import {
  CollapsibleSection,
  CurrencyMenu,
  RatingMatrix,
  ScrollDial,
} from './NodeDetailControls'
import { Checkbox, DatePicker, Radio, Textarea, TextInput, TimePicker } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { ItemTimeDisplay } from '../Timesheets/ItemTimeDisplay'

type ChecklistNodeDetailValues = Pick<
  ChecklistItem,
  | 'description'
  | 'completionCriteria'
  | 'complexityStars'
  | 'urgencyStars'
  | 'importanceStars'
  | 'estimatedHours'
  | 'actualMinutes'
  | 'actualCost'
  | 'weightage'
  | 'currencyCode'
  | 'scheduleMode'
  | 'scheduleStartDate'
  | 'scheduleEndDate'
  | 'hasTimeWindow'
  | 'scheduleTimeFrom'
  | 'scheduleTimeTo'
>

type ChecklistNodeDetailLimits = {
  titleMaxLength: number
  descriptionMaxLength: number
  completionCriteriaMaxLength: number
  estimatedHoursMax: number
  actualCostMax: number
  weightageMax: number
}

const normalizeWeightage = (weightage: unknown) =>
  typeof weightage === 'number' && Number.isFinite(weightage)
    ? Math.max(1, Math.round(weightage))
    : 1

const toDialInteger = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.round(value)) : fallback

interface ChecklistNodeCoreFieldsProps {
  titleValue: string
  values: ChecklistNodeDetailValues
  limits: ChecklistNodeDetailLimits
  disabled: boolean
  onTitleFocus?: () => void
  onTitleChange: (next: string) => void
  onTitleCommit: () => void
  onChange: (updates: Partial<ChecklistItem>) => void
}

type ChecklistNodeTitleFieldProps = Pick<
  ChecklistNodeCoreFieldsProps,
  'titleValue' | 'limits' | 'disabled' | 'onTitleFocus' | 'onTitleChange' | 'onTitleCommit'
>

export const ChecklistNodeTitleField = ({
  titleValue,
  limits,
  disabled,
  onTitleFocus,
  onTitleChange,
  onTitleCommit,
}: ChecklistNodeTitleFieldProps) => {
  const { t } = useNls()
  return (
    <label className="nodeDetailField">
      <span>{t('NLSChecklistFieldTitle')}</span>
      <TextInput
        value={titleValue}
        disabled={disabled}
        placeholder={t('NLSChecklistFieldPlaceholderTitle')}
        maxLength={limits.titleMaxLength}
        onFocus={onTitleFocus}
        onChange={(next) => onTitleChange(truncateToLimit(next, limits.titleMaxLength))}
        onBlur={onTitleCommit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur()
        }}
      />
    </label>
  )
}

type ChecklistNodeDescriptionFieldProps = Pick<
  ChecklistNodeCoreFieldsProps,
  'values' | 'limits' | 'disabled' | 'onChange'
>

export const ChecklistNodeDescriptionField = ({
  values,
  limits,
  disabled,
  onChange,
}: ChecklistNodeDescriptionFieldProps) => {
  const { t } = useNls()
  return (
    <label className="nodeDetailField">
      <span>{t('NLSChecklistFieldDescription')}</span>
      <Textarea
        value={values.description ?? ''}
        disabled={disabled}
        placeholder={t('NLSChecklistFieldPlaceholderDescription')}
        maxLength={limits.descriptionMaxLength}
        onChange={(next) => onChange({
          description: truncateToLimit(next, limits.descriptionMaxLength),
        })}
      />
    </label>
  )
}

export const ChecklistNodeCoreFields = ({
  titleValue,
  values,
  limits,
  disabled,
  onTitleFocus,
  onTitleChange,
  onTitleCommit,
  onChange,
}: ChecklistNodeCoreFieldsProps) => {
  return (
    <>
      <ChecklistNodeTitleField
        titleValue={titleValue}
        limits={limits}
        disabled={disabled}
        onTitleFocus={onTitleFocus}
        onTitleChange={onTitleChange}
        onTitleCommit={onTitleCommit}
      />
      <ChecklistNodeDescriptionField
        values={values}
        limits={limits}
        disabled={disabled}
        onChange={onChange}
      />
    </>
  )
}

interface ChecklistNodeCompletionCriteriaFieldProps {
  values: Pick<ChecklistItem, 'completionCriteria'>
  limits: Pick<ChecklistNodeDetailLimits, 'completionCriteriaMaxLength'>
  disabled: boolean
  onChange: (updates: Partial<ChecklistItem>) => void
}

export const ChecklistNodeCompletionCriteriaField = ({
  values,
  limits,
  disabled,
  onChange,
}: ChecklistNodeCompletionCriteriaFieldProps) => {
  const { t } = useNls()
  return (
    <label className="nodeDetailField">
      <span>{t('NLSChecklistFieldCompletionCriteria')}</span>
      <Textarea
        value={values.completionCriteria ?? ''}
        disabled={disabled}
        placeholder={t('NLSChecklistFieldPlaceholderCompletion')}
        maxLength={limits.completionCriteriaMaxLength}
        onChange={(next) => onChange({
          completionCriteria: truncateToLimit(next, limits.completionCriteriaMaxLength),
        })}
      />
    </label>
  )
}

interface ChecklistNodeRiskSectionProps {
  nodeId: string
  workspaceId?: string
  values: ChecklistNodeDetailValues
  limits: ChecklistNodeDetailLimits
  disabled: boolean
  defaultOpen?: boolean
  chip?: ReactNode
  impactLabel?: string
  disableScheduleFields?: boolean
  /** How the fields are wrapped. Default 'section' renders inside a
   *  CollapsibleSection titled "Risk & Resource Summary". Pass 'none' to
   *  emit just the field grid so callers can merge it into a parent
   *  section (used by the combined "Details" section in the popup). */
  wrapper?: 'section' | 'none'
  onChange: (updates: Partial<ChecklistItem>) => void
  onRatingSelect?: (key: RatingDimensionKey, star: number) => void
}

export const ChecklistNodeRiskSection = ({
  nodeId,
  workspaceId,
  values,
  limits,
  disabled,
  defaultOpen = false,
  chip,
  impactLabel,
  disableScheduleFields = false,
  wrapper = 'section',
  onChange,
  onRatingSelect,
}: ChecklistNodeRiskSectionProps) => {
  const { t } = useNls()
  const ratingValues: Record<RatingDimensionKey, number> = {
    complexity: normalizeRatingStars(values.complexityStars),
    urgency: normalizeRatingStars(values.urgencyStars),
    importance: normalizeRatingStars(values.importanceStars),
  }
  // A persisted real range always opens as Duration. This makes the detail
  // popup resilient to older documents that still say `DATED`.
  const scheduleMode: ChecklistScheduleMode = isDurationChecklist(values)
    ? 'DURATION'
    : values.scheduleMode ?? 'DATED'
  const hasTimeWindow = values.hasTimeWindow === true
  const currencyCode = values.currencyCode ?? DEFAULT_CURRENCY_CODE

  const handleRatingSelect = (key: RatingDimensionKey, star: number) => {
    if (onRatingSelect) {
      onRatingSelect(key, star)
      return
    }
    const field: keyof ChecklistItem =
      key === 'complexity' ? 'complexityStars' : key === 'urgency' ? 'urgencyStars' : 'importanceStars'
    onChange({ [field]: star } as Partial<ChecklistItem>)
  }

  const body = (
    <>
      <RatingMatrix values={ratingValues} disabled={disabled} onSelect={handleRatingSelect} />

      <div className="nodeDialRow">
        <div className="nodeDialCell">
          <span className="nodeDialLabel">{t('NLSChecklistFieldEstimatedHours')}</span>
          <ScrollDial
            ariaLabel={t('NLSChecklistFieldEstimatedHours')}
            value={toDialInteger(values.estimatedHours, 0)}
            min={0}
            max={limits.estimatedHoursMax}
            disabled={disabled}
            caption="hrs projected"
            onChange={(next) => onChange({ estimatedHours: next })}
          />
          {nodeId && (
            <ItemTimeDisplay
              workspaceId={workspaceId}
              nodeId={nodeId}
              estimatedHours={values.estimatedHours}
              actualMinutes={values.actualMinutes}
              className="nodeDialItemTimeDisplay"
            />
          )}
        </div>
        <div className="nodeDialCell">
          <span className="nodeDialLabel">{t('NLSChecklistFieldExpenseOutlay')}</span>
          <ScrollDial
            ariaLabel={t('NLSChecklistFieldExpenseOutlay')}
            value={toDialInteger(values.actualCost, 0)}
            min={0}
            max={limits.actualCostMax}
            step={getCurrencyScrollStep(currencyCode)}
            disabled={disabled}
            leading={
              <CurrencyMenu
                code={currencyCode}
                disabled={disabled}
                onSelect={(code) => onChange({ currencyCode: code })}
              />
            }
            onChange={(next) => onChange({ actualCost: next })}
          />
        </div>
        <div className="nodeDialCell">
          <span className="nodeDialLabel">{t('NLSChecklistFieldWeight')}</span>
          <ScrollDial
            ariaLabel={t('NLSChecklistFieldWeight')}
            value={normalizeWeightage(values.weightage)}
            min={1}
            max={limits.weightageMax}
            disabled={disabled}
            caption={impactLabel}
            onChange={(next) => onChange({ weightage: next })}
          />
        </div>
      </div>

      <div className="nodeScheduleBlock">
        <div className="nodeScheduleRadios" role="radiogroup" aria-label={t('NLSChecklistFieldScheduleType')}>
          {(['DATED', 'DURATION'] as const).map((mode) => (
            <span key={mode} className={`nodeScheduleRadio ${scheduleMode === mode ? 'isActive' : ''}`}>
              <Radio
                name={`schedule-${nodeId}`}
                checked={scheduleMode === mode}
                disabled={disabled || disableScheduleFields}
                label={mode === 'DATED' ? t('NLSChecklistFieldDated') : t('NLSChecklistFieldDuration')}
                onChange={() => onChange({ scheduleMode: mode })}
              />
            </span>
          ))}
        </div>

        <div className="nodeScheduleFields">
          <label className="nodeScheduleField">
            <span>{scheduleMode === 'DURATION' ? t('NLSChecklistFieldFrom') : t('NLSChecklistFieldOn')}</span>
            <DatePicker
              value={values.scheduleStartDate ?? ''}
              disabled={disabled || disableScheduleFields}
              ariaLabel={scheduleMode === 'DURATION' ? t('NLSChecklistFieldStartDate') : t('NLSChecklistAriaDateJump')}
              onChange={(raw) => onChange({ scheduleStartDate: raw || null })}
            />
          </label>
          {scheduleMode === 'DURATION' && (
            <label className="nodeScheduleField">
              <span>{t('NLSChecklistFieldBy')}</span>
              <DatePicker
                value={values.scheduleEndDate ?? ''}
                min={values.scheduleStartDate ?? undefined}
                disabled={disabled || disableScheduleFields}
                ariaLabel={t('NLSChecklistFieldEndDate')}
                onChange={(raw) => onChange({ scheduleEndDate: raw || null })}
              />
            </label>
          )}
        </div>

        <span className={`nodeScheduleTimeToggle ${hasTimeWindow ? 'isActive' : ''}`}>
          <Checkbox
            checked={hasTimeWindow}
            disabled={disabled || disableScheduleFields}
            label={t('NLSChecklistFieldTimeWindow')}
            onChange={(checked) => onChange({ hasTimeWindow: Boolean(checked) })}
          />
        </span>
        {hasTimeWindow && (
          <div className="nodeScheduleFields">
            <label className="nodeScheduleField">
              <span>{t('NLSChecklistFieldTimeFrom')}</span>
              <TimePicker
                value={values.scheduleTimeFrom ?? ''}
                disabled={disabled}
                ariaLabel={t('NLSChecklistFieldTimeFrom')}
                onChange={(val) => onChange({ scheduleTimeFrom: val || null })}
              />
            </label>
            <label className="nodeScheduleField">
              <span>{t('NLSChecklistFieldTimeTo')}</span>
              <TimePicker
                value={values.scheduleTimeTo ?? ''}
                disabled={disabled}
                ariaLabel={t('NLSChecklistFieldTimeTo')}
                onChange={(val) => onChange({ scheduleTimeTo: val || null })}
              />
            </label>
          </div>
        )}
      </div>
    </>
  )
  if (wrapper === 'none') return body
  return (
    <CollapsibleSection
      key="risk"
      title={t('NLSChecklistFieldRiskResourceSummary')}
      defaultOpen={defaultOpen}
      chip={chip}
    >
      {body}
    </CollapsibleSection>
  )
}
